<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue';
import { DraggableWrapper, useDialogFocus } from '@ilteducation/components';
import { useVideos } from '@ilteducation/videos';
import type { ContentReference } from '@ilteducation/content-collections-types';
import { parseReference } from '@ilteducation/content-collections';
import { useLocale } from '@/plugins/translation';
import ContentItem from '@/components/collections/ContentItem.vue';
import { useContentHref } from '@/composables/use-content-href';
import { useRouter } from 'vue-router';
import type { ArticleV1, Content, MaterialV1 } from '@ilteducation/content-registry-types';
import type { ContentWithDuplicates } from '@ilteducation/content';
import { useInfiniteScroll } from '@vueuse/core';
import MaterialDialog from '@/components/material/MaterialDialog.vue';

export type ContentWithStringReference = Content & { stringReference: string };

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
  (e: 'updateContent', value: ContentReference[]): Promise<void>;
  (e: 'onLoadContent'): void;
  (e: 'addContent', content: string[]): void;
}>();

type Props = {
  modelValue: string[]; // selected via checkboxes
  rounded?: boolean; // adds css rounding
  contentItems: ContentWithStringReference[]; // items for datalist
  parentScrollElement?: HTMLElement | null; // parent element for infinite scroll
  disabledToAddContent?: string[]; // content that already added in collection
  isLoading?: boolean;
  preventLoad?: boolean;
  isManaging?: boolean;
  isAdding?: boolean;
  isOrdering?: boolean;
  showHeader?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  contentItems: () => [],
  parentScrollElement: null,
  showHeader: true,
});

const { modelValue, contentItems, parentScrollElement, disabledToAddContent, isManaging, isAdding, isOrdering } =
  toRefs(props);

const onLoad = () => {
  emit('onLoadContent');
};

onMounted(() => {
  if (parentScrollElement?.value) {
    useInfiniteScroll(parentScrollElement, onLoad, { distance: 20, canLoadMore: () => !props.preventLoad });
  }
});

const hasMultipleContentTypes = computed(() => {
  const types = new Set(contentItems.value.map((c) => c.type));
  return types.size > 1;
});

const isContentInAdded = (contentId: string) => {
  return disabledToAddContent.value?.includes(contentId);
};
const addContent = (contentId: string) => {
  if (contentId) {
    emit('addContent', [contentId]);
    return;
  }
  emit('addContent', modelValue.value);
  updateModel([]);
};

const { t, l } = useLocale();

const updateModel = (value: string[]) => {
  emit('update:modelValue', value);
};

const allSelected = computed(
  () =>
    contentItems.value.length > 0 &&
    contentItems.value.every((item) => modelValue?.value.includes(item.stringReference)),
);

const selectAll = () => {
  const maxSelectedItems = 120;

  if (allSelected.value) {
    // Deselect all
    updateModel([]);
    return;
  }

  const selected = props.contentItems.slice(0, maxSelectedItems).map((c) => c.stringReference);
  updateModel(selected);
};

const toggle = (item: ContentWithStringReference) => {
  const next = [...modelValue.value];
  const idx = next.findIndex((r) => r === item.stringReference);
  if (idx >= 0) {
    next.splice(idx, 1);
  } else {
    next.push(item.stringReference);
  }
  updateModel(next);
};

const editMode = computed(() => isManaging?.value && isAdding.value !== undefined && !isAdding.value);
const isSelecting = computed(() => isManaging.value && !isOrdering.value);

const saveOrder = (ordered: ContentWithStringReference[]) => {
  emit(
    'updateContent',
    ordered.map((c) => parseReference(c.stringReference)),
  );
};

const { getContentHref, getVideoHref } = useContentHref();
const router = useRouter();
const { getVideoById } = useVideos();

const selectedMaterial = ref<ContentWithDuplicates>();
const previewDialog = ref(false);
const { rememberFocus } = useDialogFocus([previewDialog]);

const onChangeMaterial = (material: MaterialV1) => {
  selectedMaterial.value = material as ContentWithDuplicates;
};

const goToContent = async (content: ContentWithStringReference) => {
  let href;
  switch (content.type) {
    case 'ArticleV1':
      href = getContentHref(content as ArticleV1);
      if (!href) {
        return;
      }
      window.open(href);
      break;
    case 'VideoV1': {
      const videoReference = parseReference(content.stringReference);
      const video = await getVideoById(content.id);
      if (!('categoryId' in videoReference) || !video) {
        return;
      }
      href = getVideoHref(video, videoReference.categoryId);
      if (!href) {
        return;
      }
      await router.push(href);
      break;
    }
    case 'MaterialV1':
      rememberFocus();
      selectedMaterial.value = content as unknown as ContentWithDuplicates;
      previewDialog.value = true;
      break;
    default:
      return;
  }
};

const getTitle = async (item: any): Promise<string> => {
  if (item.type === 'VideoV1') {
    const video = await getVideoById(item.id);
    return video.title;
  }
  if ('title' in item && item.title) {
    return item.title;
  }
  if ('headline' in item) {
    return item.headline;
  }
  return item.id;
};

const subjectId = (content: ContentWithStringReference) => {
  const reference = parseReference(content.stringReference);
  return 'categoryId' in reference ? reference.categoryId : undefined;
};
</script>

<template>
  <div class="d-flex flex-nowrap align-start justify-start" :class="{ headers: showHeader }">
    <span v-if="showHeader" class="title my-auto">
      {{ hasMultipleContentTypes ? t(l.components.content) : t(l.videos.title) }}
    </span>
    <v-spacer />
    <div v-if="isManaging && isSelecting" class="select-button d-flex justify-center">
      <v-btn
        class="pa-0 px-1"
        :class="{
          'rounded-lg': rounded,
          'rounded-sm': !rounded,
        }"
        :disabled="isLoading"
        variant="text"
        @click="selectAll"
      >
        <span class="text-none select-all">
          {{ allSelected ? t(l.components.deselect_all) : t(l.components.select_all) }}
        </span>
      </v-btn>
    </div>
  </div>
  <v-divider />
  <DraggableWrapper
    :model-value="contentItems"
    draggableTag="ol"
    wrapperRole="listbox"
    itemRole="option"
    itemKey="stringReference"
    :disabled="!isOrdering || isAdding"
    :show-controls="false"
    @update:model-value="saveOrder"
    @clickItem="goToContent"
    :get-label="getTitle"
  >
    <template #draggableitem="{ item, grab, isGrabbed }">
      <ContentItem
        :item="item.element"
        :is-editing="editMode"
        :is-adding="isAdding"
        :is-selecting="isSelecting"
        :is-ordering="isOrdering"
        :is-selected="modelValue.includes(item.element.stringReference)"
        :is-content-in-added="isContentInAdded(item.element.stringReference)"
        :subject-id="subjectId(item.element)"
        :is-grabbed="isGrabbed"
        @add-content="addContent(item.element.stringReference)"
        @toggle-select="toggle(item.element)"
        @grab="grab()"
      />
    </template>
  </DraggableWrapper>
  <MaterialDialog
    v-if="selectedMaterial"
    :model-value="previewDialog"
    :selected-material="selectedMaterial as MaterialV1"
    @update:model-value="(val) => (previewDialog = val)"
    @update:selected-material="onChangeMaterial"
  />
</template>

<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.headers {
  height: 56px;
}

.title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.87;
}

.select-button {
  width: 150px;
  @media #{map.get(settings.$display-breakpoints, 'xs')} {
    width: 75px;
  }
}
</style>
